import React, { useEffect, useState } from 'react';
import { DefaultButton, Label, PrimaryButton, Stack, Text, TextField } from '@fluentui/react';
import IvicosStrings from 'kits/language/stringKit';
import { OrganizationDto } from 'services/client-api-wrapper/identity_provider/responses.dto';
import type { IOpenProjectConfiguration } from './openproject.types';

interface IOpenProjectSettingsFormProps {
    openProjectBaseUrl: string;
    isOpen: boolean;
    updateOpenProjectConfig: (config: IOpenProjectConfiguration) => Promise<OrganizationDto>;
    onSaveSuccess: () => void;
    onCancel: () => void;
}

// A quick client-side check only; the identity-provider is the authority (it also rejects private
// and internal addresses) and answers 400 for anything it will not store.
const looksLikeHttpsUrl = (value: string): boolean => {
    try {
        return new URL(value.trim()).protocol === 'https:';
    } catch {
        return false;
    }
};

// Shared by the save path (here) and OpenProjectSettings.tsx's deactivation path.
export function resolveOpenProjectErrorMessage(error: unknown): string {
    const status =
        typeof error === 'object' && error !== null && 'response' in error ? (error as { response?: { status?: number } }).response?.status : undefined;
    // 400 = the backend's model validation refused the base URL.
    if (status === 400) return IvicosStrings.openProjectSettingsInvalidUrlError;
    return IvicosStrings.somethingWentWrong;
}

export const OpenProjectSettingsForm: React.FC<IOpenProjectSettingsFormProps> = ({
    openProjectBaseUrl,
    isOpen,
    updateOpenProjectConfig,
    onSaveSuccess,
    onCancel
}) => {
    const [baseUrl, setBaseUrl] = useState(openProjectBaseUrl);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // The Panel is mounted while closed too, so re-sync once org data arrives.
    useEffect(() => {
        setBaseUrl(openProjectBaseUrl);
    }, [openProjectBaseUrl]);

    // Discard unsaved drafts when the panel closes.
    useEffect(() => {
        if (!isOpen) {
            setErrorMessage(null);
            setBaseUrl(openProjectBaseUrl);
            setIsSaving(false);
        }
    }, [isOpen, openProjectBaseUrl]);

    const handleSave = async (): Promise<void> => {
        if (!looksLikeHttpsUrl(baseUrl)) {
            setErrorMessage(IvicosStrings.openProjectSettingsInvalidUrlError);
            return;
        }
        setIsSaving(true);
        setErrorMessage(null);
        try {
            await updateOpenProjectConfig({ is_openproject_enable: true, openproject_base_url: baseUrl.trim() });
            onSaveSuccess();
        } catch (error) {
            console.error('Failed to save OpenProject settings:', error);
            setErrorMessage(resolveOpenProjectErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Stack verticalAlign="space-between" styles={{ root: { height: '100%', minHeight: 'calc(100vh - 110px)' } }}>
            <Stack tokens={{ childrenGap: 16 }} style={{ marginTop: 16 }}>
                <Stack tokens={{ childrenGap: 4 }}>
                    <Label>{IvicosStrings.openProjectSettingsBaseUrlLabel}</Label>
                    <TextField
                        type="url"
                        value={baseUrl}
                        placeholder="https://openproject.example.com"
                        description={IvicosStrings.openProjectSettingsBaseUrlHint}
                        onChange={(_ev, newValue) => setBaseUrl(newValue ?? '')}
                        data-test-id="openproject-base-url"
                    />
                </Stack>
                {errorMessage && (
                    <Text style={{ color: '#A80000' }} data-test-id="openproject-settings-error">
                        {errorMessage}
                    </Text>
                )}
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { width: '100%', paddingBottom: 8 } }}>
                <Stack.Item grow styles={{ root: { flexBasis: 0 } }}>
                    <DefaultButton
                        text={IvicosStrings.openProjectSettingsCancelButton}
                        onClick={onCancel}
                        styles={{ root: { width: '100%' } }}
                        data-test-id="openproject-cancel-button"
                    />
                </Stack.Item>
                <Stack.Item grow styles={{ root: { flexBasis: 0 } }}>
                    <PrimaryButton
                        text={IvicosStrings.openProjectSettingsSaveButton}
                        onClick={handleSave}
                        disabled={isSaving || !baseUrl.trim()}
                        styles={{ root: { width: '100%' } }}
                        data-test-id="openproject-save-button"
                    />
                </Stack.Item>
            </Stack>
        </Stack>
    );
};
